// MailCodeModal
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import {
    confirmEmailVerification,
    confirmPasswordReset,
    requestPasswordReset,
    sendEmailVerification
} from '../../actions/auth.action';

const initialForm = {
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
};

const getTimeText = (seconds) => {
    const safeSeconds = Math.max(Number(seconds || 0), 0);
    const minutes = Math.floor(safeSeconds / 60);
    const restSeconds = safeSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(restSeconds).padStart(2, '0')}`;
};

const getConfig = (mode) => {
    if (mode === 'emailVerification') {
        return {
            title: 'Подтверждение email',
            subtitle: 'Введите 6-значный код, который мы отправили на ваш email.',
            codeLength: 6,
            ttlSeconds: 300,
            codePlaceholder: '123456',
            submitText: 'Подтвердить email',
            requestText: 'Отправить код повторно',
            successCloseDelay: 1000,
            showEmailField: false,
            showPasswordFields: false,
        };
    }

    return {
        title: 'Восстановление пароля',
        subtitle: 'Введите email аккаунта. Мы отправим 8-значный код восстановления.',
        confirmSubtitle: 'Введите код из письма и новый пароль.',
        codeLength: 8,
        ttlSeconds: 600,
        codePlaceholder: '12345678',
        submitText: 'Изменить пароль',
        requestText: 'Получить код',
        successCloseDelay: 1200,
        showEmailField: true,
        showPasswordFields: true,
    };
};

const MailCodeModal = ({
    visible,
    onClose,
    mode = 'passwordReset',
    defaultEmail = '',
    embedded = false,
    showEmbeddedClose = false,
    codeAlreadySent = false,
    initialMessage = '',
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const config = useMemo(() => getConfig(mode), [mode]);

    const [step, setStep] = useState(mode === 'passwordReset' ? 'email' : 'confirm');
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({
        type: '',
        text: '',
    });
    const [expiresAt, setExpiresAt] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(0);

    useEffect(() => {
        if (!visible) {
            return;
        }

        setStep(mode === 'passwordReset' ? 'email' : 'confirm');
        setForm({
            ...initialForm,
            email: defaultEmail || '',
        });
        setLoading(false);

        if (codeAlreadySent) {
            const nextExpiresAt = Date.now() + config.ttlSeconds * 1000;
            setExpiresAt(nextExpiresAt);
            setSecondsLeft(config.ttlSeconds);
            setStatus({
                type: 'success',
                text: initialMessage || 'Код отправлен на email',
            });
        } else {
            setExpiresAt(null);
            setSecondsLeft(0);
            setStatus({
                type: initialMessage ? 'error' : '',
                text: initialMessage || '',
            });
        }
    }, [visible, mode, defaultEmail, codeAlreadySent, initialMessage, config.ttlSeconds]);

    useEffect(() => {
        if (!expiresAt) {
            return undefined;
        }

        const tick = () => {
            const nextSeconds = Math.max(Math.ceil((expiresAt - Date.now()) / 1000), 0);
            setSecondsLeft(nextSeconds);

            if (nextSeconds <= 0) {
                setExpiresAt(null);
            }
        };

        tick();

        const timer = setInterval(tick, 1000);

        return () => clearInterval(timer);
    }, [expiresAt]);

    if (!visible) {
        return null;
    }

    const setField = (name, value) => {
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (status.text) {
            setStatus({
                type: '',
                text: '',
            });
        }
    };

    const startTimer = () => {
        const nextExpiresAt = Date.now() + config.ttlSeconds * 1000;

        setExpiresAt(nextExpiresAt);
        setSecondsLeft(config.ttlSeconds);
    };

    const handleRequestCode = async () => {
        if (loading) {
            return;
        }

        setLoading(true);

        const result = mode === 'emailVerification'
            ? await dispatch(sendEmailVerification())
            : await dispatch(requestPasswordReset({ email: form.email }));

        setLoading(false);

        if (result.ok) {
            startTimer();
            setStep('confirm');
            setStatus({
                type: 'success',
                text: result.message,
            });
            return;
        }

        setStatus({
            type: 'error',
            text: result.secondsLeft
                ? `${result.message}. Осталось ${result.secondsLeft} сек.`
                : result.message,
        });
    };

    const handleConfirm = async () => {
        if (loading) {
            return;
        }

        if (!secondsLeft && expiresAt === null) {
            setStatus({
                type: 'error',
                text: 'Срок действия кода истёк. Отправьте код повторно.',
            });
            return;
        }

        setLoading(true);

        const result = mode === 'emailVerification'
            ? await dispatch(confirmEmailVerification({ code: form.code }))
            : await dispatch(confirmPasswordReset({
                email: form.email,
                code: form.code,
                newPassword: form.newPassword,
                confirmPassword: form.confirmPassword,
            }));

        setLoading(false);

        if (result.ok) {
            setStatus({
                type: 'success',
                text: result.message,
            });

            setExpiresAt(null);
            setSecondsLeft(0);

            if (onSuccess) {
                onSuccess(result);
            }

            if (!embedded && onClose) {
                setTimeout(() => {
                    onClose();
                }, config.successCloseDelay);
            }

            return;
        }

        setStatus({
            type: 'error',
            text: result.attemptsLeft !== undefined
                ? `${result.message}. Осталось попыток: ${result.attemptsLeft}`
                : result.message,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (mode === 'passwordReset' && step === 'email') {
            handleRequestCode();
            return;
        }

        handleConfirm();
    };

    const percent = expiresAt ? Math.max(Math.min((secondsLeft / config.ttlSeconds) * 100, 100), 0) : 0;
    const expired = step === 'confirm' && !secondsLeft && !expiresAt;
    const rootClassName = embedded ? 'mailCodeModal mailCodeModal--embedded' : 'mailCodeModal';

    const content = (
        <form
            className="mailCodeModal__content"
            onSubmit={handleSubmit}
        >
            {(!embedded || showEmbeddedClose) && onClose ? (
                <button
                    type="button"
                    className="mailCodeModal__close"
                    onClick={onClose}
                    aria-label="Закрыть окно подтверждения"
                    title="Закрыть"
                >
                    <CloseIcon />
                </button>
            ) : null}

            <div className="mailCodeModal__header">
                <div className="mailCodeModal__title">
                    {config.title}
                </div>

                <div className="mailCodeModal__subtitle">
                    {mode === 'passwordReset' && step === 'confirm'
                        ? config.confirmSubtitle
                        : config.subtitle}
                </div>
            </div>

            <div className="mailCodeModal__body">
                {config.showEmailField && (
                    <label className="mailCodeModal__field">
                        <span>Email</span>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setField('email', e.target.value)}
                            placeholder="user@mail.ru"
                            autoComplete="email"
                            disabled={loading || step === 'confirm'}
                        />
                    </label>
                )}

                {!config.showEmailField && defaultEmail && (
                    <div className="mailCodeModal__emailInfo">
                        <span>Email</span>
                        <strong>{defaultEmail}</strong>
                    </div>
                )}

                {step === 'confirm' && (
                    <>
                        <label className="mailCodeModal__field">
                            <span>Код из письма</span>
                            <input
                                type="text"
                                inputMode="numeric"
                                value={form.code}
                                onChange={(e) => setField('code', e.target.value.replace(/\D/g, '').slice(0, config.codeLength))}
                                placeholder={config.codePlaceholder}
                                autoComplete="one-time-code"
                                disabled={loading}
                            />
                        </label>

                        <div className={expired ? 'mailCodeModal__timer mailCodeModal__timer--expired' : 'mailCodeModal__timer'}>
                            <div className="mailCodeModal__timerTop">
                                <span>{expired ? 'Код устарел' : 'Код действует'}</span>
                                <strong>{getTimeText(secondsLeft)}</strong>
                            </div>

                            <div className="mailCodeModal__timerLine">
                                <span style={{ width: `${percent}%` }} />
                            </div>
                        </div>

                        {config.showPasswordFields && (
                            <>
                                <label className="mailCodeModal__field">
                                    <span>Новый пароль</span>
                                    <input
                                        type="password"
                                        value={form.newPassword}
                                        onChange={(e) => setField('newPassword', e.target.value)}
                                        placeholder="Минимум 6 символов"
                                        autoComplete="new-password"
                                        disabled={loading}
                                    />
                                </label>

                                <label className="mailCodeModal__field">
                                    <span>Повторите новый пароль</span>
                                    <input
                                        type="password"
                                        value={form.confirmPassword}
                                        onChange={(e) => setField('confirmPassword', e.target.value)}
                                        placeholder="Повторите пароль"
                                        autoComplete="new-password"
                                        disabled={loading}
                                    />
                                </label>
                            </>
                        )}
                    </>
                )}

                {status.text && (
                    <div className={`mailCodeModal__status mailCodeModal__status--${status.type}`}>
                        {status.text}
                    </div>
                )}
            </div>

            <div className="mailCodeModal__footer">
                {mode === 'passwordReset' && step === 'confirm' && (
                    <button
                        type="button"
                        className="mailCodeModal__linkButton"
                        onClick={() => {
                            setStep('email');
                            setExpiresAt(null);
                            setSecondsLeft(0);
                            setStatus({
                                type: '',
                                text: '',
                            });
                        }}
                        disabled={loading}
                    >
                        Изменить email
                    </button>
                )}

                {step === 'confirm' && (
                    <button
                        type="button"
                        className="mailCodeModal__linkButton"
                        onClick={handleRequestCode}
                        disabled={loading || secondsLeft > config.ttlSeconds - 60}
                    >
                        Отправить код повторно
                    </button>
                )}

                <button
                    type="submit"
                    className="mailCodeModal__submit"
                    disabled={loading}
                >
                    {loading
                        ? 'Подождите...'
                        : step === 'email'
                            ? config.requestText
                            : config.submitText}
                </button>
            </div>
        </form>
    );

    if (embedded) {
        return (
            <div className={rootClassName}>
                {content}
            </div>
        );
    }

    return (
        <div className={rootClassName}>
            <div
                className="mailCodeModal__backdrop"
                onClick={onClose}
            />
            {content}
        </div>
    );
};

export default MailCodeModal;
